const RECORDINGS_DB_NAME = 'dirtylingo-recordings';
const RECORDINGS_DB_VERSION = 1;
const RECORDINGS_STORE = 'recordings';

let recordingsDbPromise: Promise<IDBDatabase> | null = null;

type RecordingEntry = {
  id: string;
  file: File;
  updatedAt: number;
};

const requestPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionPromise = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

const openRecordingsDb = (): Promise<IDBDatabase> => {
  if (!recordingsDbPromise) {
    recordingsDbPromise = new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not available'));
        return;
      }
      const request = indexedDB.open(RECORDINGS_DB_NAME, RECORDINGS_DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(RECORDINGS_STORE)) {
          db.createObjectStore(RECORDINGS_STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return recordingsDbPromise;
};

export const getRecording = async (id: string): Promise<File | null> => {
  try {
    const db = await openRecordingsDb();
    const transaction = db.transaction(RECORDINGS_STORE, 'readonly');
    const store = transaction.objectStore(RECORDINGS_STORE);
    const result = await requestPromise(store.get(id));
    return (result as RecordingEntry | undefined)?.file ?? null;
  } catch {
    return null;
  }
};

export const setRecording = async (id: string, file: File): Promise<void> => {
  try {
    const db = await openRecordingsDb();
    const transaction = db.transaction(RECORDINGS_STORE, 'readwrite');
    const store = transaction.objectStore(RECORDINGS_STORE);
    await requestPromise(store.put({ id, file, updatedAt: Date.now() }));
    await transactionPromise(transaction);
  } catch {
    return;
  }
};

export const deleteRecordings = async (ids: string[]): Promise<void> => {
  if (ids.length === 0) {
    return;
  }
  try {
    const db = await openRecordingsDb();
    const transaction = db.transaction(RECORDINGS_STORE, 'readwrite');
    const store = transaction.objectStore(RECORDINGS_STORE);
    for (const id of ids) {
      store.delete(id);
    }
    await transactionPromise(transaction);
  } catch {
    return;
  }
};

export const listRecordingIds = async (): Promise<string[]> => {
  try {
    const db = await openRecordingsDb();
    const transaction = db.transaction(RECORDINGS_STORE, 'readonly');
    const store = transaction.objectStore(RECORDINGS_STORE);
    const result = await requestPromise(store.getAllKeys());
    return (result as string[]) ?? [];
  } catch {
    return [];
  }
};
